import {
  Directive,
  effect,
  inject,
  Input,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { PermissionService } from '../../core/services';

/**
 * Структурная директива: показывает содержимое, только если у пользователя есть право (или одно из прав).
 * Использование: `*pkpgHasPermission="'tariffs:manage'"` или `*pkpgHasPermission="['a', 'b']"`.
 * Это UX-скрытие — настоящий барьер обеспечивает backend.
 */
@Directive({
  // Конфиг directive-selector в parking-gate требует префикс 'pkp-g' И camelCase одновременно,
  // что для attribute-директивы невыполнимо (дефис несовместим с camelCase). Используем
  // camelCase-селектор с префиксом pkpg и точечно отключаем правило только на этой строке.
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[pkpgHasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly permissionService = inject(PermissionService);

  private readonly required = signal<string[]>([]);
  private hasView = false;

  @Input()
  set pkpgHasPermission(value: string | string[] | null | undefined) {
    this.required.set(value == null ? [] : Array.isArray(value) ? value : [value]);
  }

  constructor() {
    // Гарантируем, что права загружены (кэшируется, повторные вызовы дешёвые).
    this.permissionService.ensureLoaded().subscribe();

    // Реагируем и на смену требуемых прав, и на загрузку/обновление прав пользователя.
    effect(() => {
      const keys = this.required();
      const permissions = this.permissionService.permissions();
      const granted = keys.length === 0 || keys.some(key => permissions.has(key));

      if (granted && !this.hasView) {
        this.viewContainer.createEmbeddedView(this.templateRef);
        this.hasView = true;
      } else if (!granted && this.hasView) {
        this.viewContainer.clear();
        this.hasView = false;
      }
    });
  }
}
